import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'stories/components'
import { InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

export const SelectModal = ({
  options,
  value,
  title,
  multiple,
  searchable,
  ...props
}: {
  title: string
  options: Record<string, string>
  value: string[] | string
  multiple: boolean
  searchable?: boolean
  onSubmit: Function
  onClose: Function
}) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [search, setSearch] = useState('')

  useEffect(() => {
    const tempInputs: Record<string, InputType> = {}

    Object.keys(options).forEach((key) => {
      if (!options[key]) return
      tempInputs[key] = {
        inputType: 'CheckBox',
        title: options[key],
        value: Array.isArray(value) ? value.includes(key) : options[key] === value,
      }
    })

    setInputs(tempInputs)
  }, [options])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)

    // Choose only one item
    if (!multiple) {
      if (newInputs[key].value) {
        Object.keys(newInputs).forEach((v) => {
          if (v !== key) newInputs[v].value = false
        })
      }
    }

    setInputs(newInputs)
  }

  const onSubmit = async () => {
    const value = Object.keys(inputs)
      .map((key) => {
        if (inputs[key].value) return key
        return null
      })
      .filter((value) => !!value)

    props.onSubmit(multiple ? value : value[0])
  }

  const onClose = () => {
    props.onClose()
  }

  const onSearchChange = (value: string) => {
    setSearch(value)
  }

  // Rendered into `body` because `InputWithButton` is often used inside a draggable Modal, and a
  // dragged ancestor's `transform` becomes the containing block for this modal's fixed positioning.
  return createPortal(
    <Modal title={title} titleOkay="Confirm" onClose={onClose} onOk={onSubmit} isOpen childLevel={1}>
      <div className="w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {searchable && (
          <div className="input md:col-span-2 border-b sticky top-0 bg-white z-10">
            <RenderInput
              input={{
                inputType: 'text',
                title: 'Search',
                value: search,
                type: 'text',
                placeholder: 'Search...',
              }}
              onChange={(_: string, value: string) => onSearchChange(value)}
              Key="search"
            />
          </div>
        )}

        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          if (searchable && !input.title.toLowerCase().includes(search.toLowerCase())) return null

          return (
            <div className={`input md:col-span-${input.span || 1} border-b`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
    </Modal>,
    document.body,
  )
}
